import axios, { AxiosHeaders, type InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';

export const CSRF_COOKIE_KEY = 'csrf_token';
export const CSRF_HEADER = 'X-CSRF-Token';

let cachedCsrf: string | undefined;

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

const isDevelopment = import.meta.env.DEV;
const isLocalhost =
  typeof window !== 'undefined' && window.location.hostname === 'localhost';
const isAppSubdomain =
  typeof window !== 'undefined' &&
  /^(profile|admin)\.(dev\.)?myteacher\.pro$/.test(window.location.hostname);

const csrfBaseURL =
  isLocalhost && isDevelopment ? '/v1' : isAppSubdomain ? '/v1' : '/v1';

const csrfClient = axios.create({
  baseURL: csrfBaseURL,
  withCredentials: true,
  timeout: 12_000,
});

export const readCsrfFromHeaders = (headers: unknown): string | undefined => {
  if (!headers || typeof headers !== 'object') {
    return undefined;
  }
  const h = headers as Record<string, unknown> & {
    get?: (name: string, matcher?: unknown) => unknown;
  };
  const fromGetter = typeof h.get === 'function' ? h.get(CSRF_HEADER) : undefined;
  if (typeof fromGetter === 'string') {
    return fromGetter;
  }
  const raw = h[CSRF_HEADER] ?? h[CSRF_HEADER.toLowerCase()];
  return typeof raw === 'string' ? raw : undefined;
};

export const clearCsrfCache = (): void => {
  cachedCsrf = undefined;
  Cookies.remove(CSRF_COOKIE_KEY, { path: '/' });
};

export const getCsrfToken = (): string | undefined => {
  return cachedCsrf;
};

export const rememberCsrfFromResponse = (token?: string | null): void => {
  if (token) {
    cachedCsrf = token;
  }
};

export const ensureCsrfToken = async (force = false): Promise<string | undefined> => {
  if (!force) {
    const existing = getCsrfToken();
    if (existing) {
      return existing;
    }
  } else {
    clearCsrfCache();
  }

  const response = await csrfClient.get<{ csrf_token?: string }>('/auth/csrf/');
  const token =
    response.data?.csrf_token ||
    readCsrfFromHeaders(response.headers) ||
    Cookies.get(CSRF_COOKIE_KEY);
  rememberCsrfFromResponse(token);
  return token;
};

export const shouldAttachCsrf = (method?: string): boolean => {
  return MUTATING_METHODS.has((method || 'get').toLowerCase());
};

/** Axios 1.x uses AxiosHeaders; bracket assignment does not attach to outgoing requests. */
export const attachCsrfHeader = (
  headers: InternalAxiosRequestConfig['headers'] | undefined,
  token: string,
): InternalAxiosRequestConfig['headers'] => {
  const h = headers ?? new AxiosHeaders();
  if (typeof (h as AxiosHeaders).set === 'function') {
    (h as AxiosHeaders).set(CSRF_HEADER, token);
  } else {
    (h as Record<string, string>)[CSRF_HEADER] = token;
  }
  return h;
};
